/**
 * External Imports
 */
import { FC, useEffect, useMemo, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

/**
 * Internal Imports
 */
import Table from '../../../../shared/table';
import {
  clearPatientRoundHistory,
  setPatientRoundsReportSort,
  clearTasksByAccountNum
} from '../../../../../redux/actions';
// import { getRound } from '../../../redux/actions/Round.action';

import { reverseName, toTitleCase } from '../../../../../utils/helpers';

/**
 * Global Type Definition Imports
 */
import { Patient, PatientRoundsReport } from '../../../../../config/interfaces';

/**
 * Style Imports
 */
import {
  CellContainer,
  StyledLabel,
  StyledNeuTableRow
} from '../../../../shared/table/Table.styles';

interface IPatientRoundsReportTableProps {
  loading: boolean;
  roundsReportList: PatientRoundsReport[];
  totalTableCount: number;
}
const PatientRoundsReportTable: FC<IPatientRoundsReportTableProps> = ({
  loading,
  roundsReportList,
  totalTableCount
}) => {
  const dispatch = useDispatch();

  const [patientToOpen, setPatientToOpen] = useState<
    PatientRoundsReport | Patient | null
  >(null);
  const [sortKey, setSortKey] = useState<string>('roundDate');
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>('desc');
  const [active, setActive] = useState<string>('Date');

  const firstUpdate: { current: boolean } = useRef(true); // to detect first render

  const navigate = useNavigate();

  const handleSort = (column: string) => {
    let key;
    switch (column) {
      case 'Unit':
        key = 'unit';
        break;
      case 'Room':
        key = 'room';
        break;
      case 'Patient & MRN':
        key = 'patientName';
        break;
      case 'Status':
        key = 'status';
        break;
      case 'Rounded By':
        key = 'roundedBy';
        break;
      case 'Date':
        key = 'roundDate';
        break;
      default:
        key = '';
    }
    if (key === sortKey && sortDir === 'asc') {
      setSortDir('desc');
    } else {
      setSortDir('asc');
    }
    setActive(column);
    setSortKey(key);
  };

  const openPatient = async (round: PatientRoundsReport) => {
    setPatientToOpen(round);
    // TODO: Add later with round refactor Phase II if patient loading isn't in profile
    // if (round.roundId) {
    //   dispatch(
    //     getRound({
    //       roundId: round.roundId,
    //       fromHistoryDrawer: false,
    //       type: 'patient',
    //       accountNum: round.accountNum,
    //       unitId: round.unitId
    //     })
    //   );
    // }
  };

  const isTableEmpty = useMemo(
    () => roundsReportList.length === 0,
    [roundsReportList]
  );

  useEffect(() => {
    return () => {
      dispatch(
        setPatientRoundsReportSort({ sortKey: 'roundDate', sortDir: 'desc' })
      );
    };
  }, []);

  // TODO: Can this be done in a saga to not repeat since its repeated across to encounters
  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }
    if (patientToOpen) {
      dispatch(clearPatientRoundHistory());
      dispatch(clearTasksByAccountNum());

      const patientMeta = {
        facilityId: patientToOpen?.facilityId,
        unitId: patientToOpen?.unitId,
        urn: patientToOpen?.patientId,
        mrn: patientToOpen?.patientMRN,
        accountNum: patientToOpen?.accountNumber,
        room: patientToOpen?.room,
        bed: patientToOpen?.bed,
        firstName: patientToOpen.patientFirstName,
        lastName: patientToOpen.patientLastName
      };
      if ((patientToOpen.accountNumber as string).substring(0, 2) === 'UL') {
        navigate('/patients/profile', {
          state: {
            patientMeta: {
              ...patientMeta,
              lastRoundId: patientToOpen.roundId
            },
            isUnlistedPatient: true
          }
        });
      } else {
        navigate('/patients/profile', {
          state: {
            patientMeta,
            isUnlistedPatient: false
          }
        });
      }
    }
  }, [patientToOpen]);

  useEffect(() => {
    dispatch(setPatientRoundsReportSort({ sortKey, sortDir }));
  }, [sortDir, sortKey]);

  const headers: JSX.Element[] = [
    // columns="{'Status': '14%', 'Rounded By': '18%', 'Date': '9%', 'Time':'9%'}"
    <CellContainer
      key="Unit"
      id="Patient-Rounds-Report-Table-Unit-Column"
      unit="column"
      colWidth={20}
      direction={!(active === 'Unit') ? 'none' : sortDir}
      sort
    >
      Unit
    </CellContainer>,
    <CellContainer
      key="Room"
      id="Patient-Rounds-Report-Table-Room-Column"
      unit="column"
      colWidth={10}
      direction={!(active === 'Room') ? 'none' : sortDir}
      sort
    >
      Room
    </CellContainer>,
    <CellContainer
      key="Patient & MRN"
      id="Patient-Rounds-Report-Table-Patient-Column"
      unit="column"
      colWidth={20}
      direction={!(active === 'Patient & MRN') ? 'none' : sortDir}
      sort
    >
      Patient & MRN
    </CellContainer>,
    <CellContainer
      key="Status"
      id="Patient-Rounds-Report-Table-Status-Column"
      unit="column"
      colWidth={14}
      direction={!(active === 'Status') ? 'none' : sortDir}
      sort
    >
      Status
    </CellContainer>,
    <CellContainer
      key="Rounded By"
      id="Patient-Rounds-Report-Table-RoundedBy-Column"
      unit="column"
      colWidth={18}
      direction={!(active === 'Rounded By') ? 'none' : sortDir}
      sort
    >
      Rounded By
    </CellContainer>,
    <CellContainer
      key="Date"
      id="Patient-Rounds-Report-Table-Date-Column"
      unit="column"
      colWidth={9}
      direction={!(active === 'Date') ? 'none' : sortDir}
      sort
    >
      Date
    </CellContainer>,
    <CellContainer
      key="Time"
      id="Patient-Rounds-Report-Table-Time-Column"
      unit="column"
      colWidth={9}
      direction="none"
    >
      Time
    </CellContainer>
  ];

  const rows = roundsReportList?.map(
    (round: PatientRoundsReport, i: number) => {
      return (
        <StyledNeuTableRow
          // eslint-disable-next-line react/no-array-index-key
          key={`Patient-Rounds-Report-Row-${i}`}
          id={`Patient-Rounds-Report-Row-${round.roundId}`}
          unit="row"
          size="default"
          height="5em"
        >
          <CellContainer unit="column" colWidth={20}>
            <StyledLabel style={{ whiteSpace: 'normal' }}>
              <div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    color: 'black',
                    fontWeight: 700
                  }}
                >
                  {round.unit}
                </div>
                <div> {round.unitId}</div>
              </div>
            </StyledLabel>
          </CellContainer>
          <CellContainer unit="column" colWidth={10}>
            <StyledLabel style={{ whiteSpace: 'normal' }}>
              <div>{round.room}</div>
            </StyledLabel>
          </CellContainer>
          <CellContainer unit="column" colWidth={20}>
            <StyledLabel
              style={{ cursor: 'pointer', whiteSpace: 'normal' }}
              onClick={() => openPatient(round)}
            >
              <div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    color: '#206DA3',
                    fontWeight: 700
                  }}
                >
                  {toTitleCase(
                    reverseName(round.patientFirstName, round.patientLastName)
                  )}
                </div>
                <div>{round.patientMRN}</div>
              </div>
            </StyledLabel>
          </CellContainer>
          <CellContainer unit="column" colWidth={14}>
            <StyledLabel>{round.status}</StyledLabel>
          </CellContainer>
          <CellContainer unit="column" colWidth={18}>
            <StyledLabel style={{ whiteSpace: 'normal' }}>
              <div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    color: 'black',
                    fontWeight: 700
                  }}
                >
                  {round.createdUserName}
                </div>
                <div> {round.createdUserId}</div>
              </div>
            </StyledLabel>
          </CellContainer>
          <CellContainer unit="column" colWidth={9}>
            {/* <StyledLabel slot="Status">{round.status}</StyledLabel> */}
            <StyledLabel>{round.date}</StyledLabel>
          </CellContainer>
          <CellContainer unit="column" colWidth={9}>
            <StyledLabel>{round.time}</StyledLabel>
          </CellContainer>
        </StyledNeuTableRow>
      );
    }
  );

  return (
    <Table
      customClass="patient-rounds-report-table"
      headers={headers}
      rows={rows}
      isTableEmpty={isTableEmpty}
      loading={loading}
      totalResults={totalTableCount}
      type="patientRoundsReport"
      onSort={(e: any) => handleSort(e.detail.label)}
    />
  );
};

export default PatientRoundsReportTable;
